import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, generatePath, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import GlobalStyles from '../../../../styles/GlobalStyles.web';
import { BusinessRouterPaths } from '../../../page/Routes/RouterPaths';
import { handleError, checkStatus, parseJSON } from '../../../shared/api/core';
import {
  checkForRemoteConflictsOrder,
  getCustomerOrderDetail,
  updateCustomerOrderById,
} from '../../api';
import {
  saveCustomerOrderConflictRes,
  clearSelectedCustomerOrderData as clearSelectedCustomerOrder,
} from '../../../master-data/data/actions/customerOrderActions';
import { API_RESPONSE } from '../../../shared/lib/Localization';
import { enqueueSnackbar, ToastError, ToastSuccess } from '../../../shared/components/Notifier';
import { getStatusTextForQty } from '../../lib/orderPickingItems';
import { mapItemDetails } from '../../lib/orderPickingItems';

const OrderPickingSummary = (props: any) => {
  const history = useHistory();
  const location = useLocation<any>();
  const { orderId } = useParams<{ orderId: string }>();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [summaryItems, setSummaryItems] = useState<any[] | null>(null);
  const [summaryDeletedItems, setSummaryDeletedItems] = useState<any[] | null>(null);
  const [updatedOrder, setUpdatedOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [isReadyForPickup, setReadyForPickup] = useState(false);
  const [isDonePicking, setDonePicking] = useState(false);
  const [isUpdatedCustomerOrder, setUpdatedCustomerOrder] = useState(false);
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [enableDonePicking, setEnableDonePicking] = useState(false);
  const [canFinalizeOrder, setCanFinalizeOrder] = useState(false);

  const co = props.customerOrders;
  const sb = props.userData?.selectedBusiness;
  const locationId = props.userData?.selectedLocations?.[0]?.id;

  useEffect(() => {
    const snapshot = location?.state?.summarySnapshot;
    if (!snapshot || !Array.isArray(snapshot) || snapshot.length === 0) {
      return;
    }
    const deletedSnapshot = location?.state?.summaryDeletedSnapshot;
    setSummaryItems(snapshot);
    setSummaryDeletedItems(Array.isArray(deletedSnapshot) ? deletedSnapshot : null);
    setCanFinalizeOrder(!!location?.state?.canFinalizeOrder);
    const hasPicked = snapshot.find((i: any) => Number(i.pickedQty) > 0);
    setEnableDonePicking(!hasPicked);
  }, [location]);

  useEffect(() => {
    const products = co?.customerOrderSummaryItems?.products;
    if (location?.state?.summarySnapshot?.length) {
      return;
    }
    const deleted = co?.customerOrderSummaryItems?.deletedProducts;
    const canFin = co?.customerOrderSummaryItems?.canFinalizeOrder;
    setSummaryItems(products || null);
    setSummaryDeletedItems(deleted || null);
    setCanFinalizeOrder(!!canFin);
    setUpdatedCustomerOrder(false);
    setSaveBtnDisabled(false);
    if (products) {
      const hasPicked = products.find((i: any) => Number(i.pickedQty) > 0);
      setEnableDonePicking(!hasPicked);
    }
  }, [co?.customerOrderSummaryItems, location]);

  useEffect(() => {
    const productsFromStore = co?.customerOrderSummaryItems?.products;
    if (location?.state?.summarySnapshot?.length) {
      return;
    }
    if (productsFromStore && productsFromStore.length > 0) {
      return;
    }
    if (summaryItems && summaryItems.length > 0) {
      return;
    }
    if (!orderId || !sb?.business_id || !locationId) {
      return;
    }
    setLoading(true);
    getCustomerOrderDetail(orderId, sb.business_id, String(locationId))
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then((res: any) => {
        const orderData = res?.order_data;
        const items = orderData?.items || [];
        const updatedData = items.filter((i: any) => !i.is_deleted).map((i: any) => mapItemDetails(i));
        const deletedData = items.filter((i: any) => i.is_deleted).map((i: any) => mapItemDetails(i));
        setSummaryItems(updatedData);
        setSummaryDeletedItems(deletedData);
        setCanFinalizeOrder(!!res?.pick_group?.can_finalize_order);
        const hasPicked = updatedData.find((i: any) => Number(i.pickedQty) > 0);
        setEnableDonePicking(!hasPicked);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        props.showToast({
          message: 'Failed to load order summary.',
          options: ToastError,
        });
      });
  }, [orderId, sb?.business_id, locationId, summaryItems, co?.customerOrderSummaryItems, location]);

  useEffect(() => {
    if (!summaryItems?.length) {
      return;
    }
    const orderDetails: any = {
      is_finalized: isDonePicking,
      is_ready_for_pickup: isReadyForPickup,
    };
    const items = summaryItems.map((row: any) => ({
      id: row.id,
      picked_quantity: Number(
        row.pickedQty === '' || row.pickedQty === '0' ? '0' : row.pickedQty,
      ),
      done_picked_qty: row.confirmed,
      code_quantities: (row.codePickedQtys || []).map((cq: any) => ({
        ...cq,
        picked_quantity: cq.pickedQty,
      })),
    }));
    orderDetails.ordered_items = items;
    setUpdatedOrder(orderDetails);
  }, [summaryItems, isReadyForPickup, isDonePicking]);

  const selectedItem = co?.selectedCustomerOrder?.item;
  const getOrderIdentifiers = () => ({
    order_id: selectedItem?.order_id ?? Number(orderId),
    business_id: selectedItem?.business ?? sb?.business_id,
    location_id: selectedItem?.location ?? locationId,
  });

  const checkForConflicts = () => {
    if (!selectedItem || !summaryItems) {
      const fallback = getOrderIdentifiers();
      if (!summaryItems || !fallback.order_id || !fallback.business_id || !fallback.location_id) {
        return;
      }
    }
    setLoading(true);
    const { order_id, business_id, location_id } = getOrderIdentifiers();
    const temp_items = summaryItems.map((item: any) => ({
      id: item.id,
      picked_quantity: Number(
        item.pickedQty === '' || item.pickedQty === '0' ? '0' : item.pickedQty,
      ),
      done_picked_qty: item.confirmed,
      code_quantities: (item.codePickedQtys || []).map((cq: any) => ({
        ...cq,
        picked_quantity: cq.pickedQty,
      })),
    }));
    const data = { ordered_items: temp_items };
    checkForRemoteConflictsOrder(order_id, business_id, location_id, data)
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then((res: any) => {
        setLoading(false);
        if (res.status === 'conflicted') {
          props.saveCustomerOrderConflictRes(res);
          const path = generatePath(BusinessRouterPaths.customerOrderPickingDetail, {
            orderId: String(order_id),
          });
          history.push(path);
        } else {
          handleSave();
        }
      })
      .catch(() => {
        setLoading(false);
        props.showToast({
          message: 'Error checking conflicts with the remote server',
          options: ToastError,
        });
      });
  };

  const handleSave = () => {
    const { order_id, business_id, location_id } = getOrderIdentifiers();
    if (!updatedOrder || !order_id || !business_id || !location_id) {
      return;
    }
    setLoading(true);
    setSaveBtnDisabled(true);
    updateCustomerOrderById(updatedOrder, order_id, business_id, location_id)
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then(() => {
        setLoading(false);
        props.showToast({
          message: API_RESPONSE.UPDATE_CUSTOMER_ORDER_SUCCESS,
          options: ToastSuccess,
        });
        setUpdatedCustomerOrder(true);
      })
      .catch((error: any) => {
        setLoading(false);
        setSaveBtnDisabled(false);
        props.showToast({
          message: API_RESPONSE.UPDATE_CUSTOMER_ORDER_FAILURE,
          options: ToastError,
        });
      });
  };

  const changeDonePicking = () => {
    if (!isDonePicking) {
      const allDone = summaryItems?.map((s) => s.confirmed).reduce((a, b) => a && b, true);
      if (!allDone) {
        props.showToast({
          message: 'Please mark all items as done',
          options: ToastError,
        });
        return;
      }
    }
    if (isDonePicking) {
      setDonePicking(false);
      setReadyForPickup(false);
    } else {
      setShowConfirmModal(true);
    }
  };

  const changeReadyForPickup = () => {
    if (!isReadyForPickup && !isDonePicking) {
      props.showToast({
        message: 'Please check Done Picking first',
        options: ToastError,
      });
      return;
    }
    setReadyForPickup(!isReadyForPickup);
  };

  const clearAndNavigateBack = () => {
    props.clearSelectedCustomerOrder();
    history.push(BusinessRouterPaths.customerOrderList);
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        px={1}
        py={1}
        bgcolor={GlobalStyles.primaryColor}
        color="#fff"
      >
        <IconButton color="inherit" onClick={() => history.goBack()}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" style={{ flex: 1 }}>
          Pickup status & save
        </Typography>
        <IconButton color="inherit" onClick={() => setInfoOpen(true)}>
          <InfoOutlinedIcon />
        </IconButton>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      <Box px={isSmDown ? 1 : 2} py={2}>
        {summaryItems && (
          <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={12} mb={2}>
            {canFinalizeOrder && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isDonePicking}
                    onChange={changeDonePicking}
                    disabled={enableDonePicking || saveBtnDisabled}
                    color="primary"
                  />
                }
                label="Done picking"
              />
            )}
            {canFinalizeOrder && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isReadyForPickup}
                    onChange={changeReadyForPickup}
                    disabled={enableDonePicking || saveBtnDisabled}
                    color="primary"
                  />
                }
                label="Ready for pickup"
              />
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={saveBtnDisabled}
              onClick={checkForConflicts}
              style={{ minHeight: 44 }}
            >
              Save
            </Button>
          </Box>
        )}

        <Box display="flex" fontWeight="bold" flexWrap="wrap" pb={1} borderBottom="1px solid #ddd">
          <Typography style={{ flex: '2 1 120px' }}>Item</Typography>
          <Typography style={{ flex: '0 0 40px', textAlign: 'center' }}>Done</Typography>
          <Typography style={{ flex: '0 0 40px', textAlign: 'center' }}>Ord</Typography>
          <Typography style={{ flex: '0 0 40px', textAlign: 'center' }}>Pck</Typography>
          <Typography style={{ flex: '1 1 80px', textAlign: 'center' }}>Status</Typography>
        </Box>

        {summaryItems
          ?.filter((i) => !i.is_deleted)
          .map((item: any, index: number) => (
            <Box
              key={item.id}
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              py={0.5}
              borderBottom="1px solid #eee"
            >
              <Typography style={{ flex: '2 1 120px', wordBreak: 'break-word' }}>
                {index + 1}. {item.product?.name}
              </Typography>
              <Typography
                style={{
                  flex: '0 0 40px',
                  textAlign: 'center',
                  color: item.confirmed ? GlobalStyles.green : GlobalStyles.red,
                }}
              >
                {item.confirmed ? '✓' : '✗'}
              </Typography>
              <Typography style={{ flex: '0 0 40px', textAlign: 'center' }}>
                {item.quantity}
              </Typography>
              <Typography style={{ flex: '0 0 40px', textAlign: 'center' }}>
                {item.pickedQty === '' ? 0 : item.pickedQty}
              </Typography>
              <Typography style={{ flex: '1 1 80px', textAlign: 'center' }}>
                {getStatusTextForQty(item)}
              </Typography>
            </Box>
          ))}

        {isUpdatedCustomerOrder && (
          <Card style={{ marginTop: 16 }}>
            <CardContent>
              <Typography color="primary" gutterBottom>
                Success
              </Typography>
              <Typography variant="body2">
                Customer order {selectedItem?.order_number || orderId} was updated successfully.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                style={{ marginTop: 12, minHeight: 44 }}
                onClick={clearAndNavigateBack}
              >
                OK
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Status legend</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Over — picked greater than ordered
          </Typography>
          <Typography variant="body2" gutterBottom>
            Ok — picked equals ordered
          </Typography>
          <Typography variant="body2">Short — ordered greater than picked</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <DialogTitle>Finalize order</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You are about to finalize the order; it cannot be modified later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setShowConfirmModal(false);
              setDonePicking(true);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapState = (state: any) => ({
  userData: state.userLocalData,
  customerOrders: state.customerOrderModuleData?.customerOrderList,
});

const mapDispatch = (dispatch: any) => ({
  saveCustomerOrderConflictRes: (d: any) => dispatch(saveCustomerOrderConflictRes(d)),
  clearSelectedCustomerOrder: () => dispatch(clearSelectedCustomerOrder()),
  showToast: (t: any) => dispatch(enqueueSnackbar(t)),
});

export default connect(mapState, mapDispatch)(OrderPickingSummary);
